import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChange } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { debounceTime } from 'rxjs/operators';
import english from '../../../../main/maintenances/i18n/en.json';
import spanish from '../../../../main/maintenances/i18n/es.json';
import portuguese from '../../../../main/maintenances/i18n/pt.json';
import { EquipmentsApi } from '../../../api/equipments/equipments.api';
import { Equipment, Segment } from '../../../interfaces';

export function ValidateAutocomplete(): object {
  return { required: true };
}

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => EquipmentsComponent),
  multi: true
};

@Component({
  selector: 'equipments',
  templateUrl: './equipments.component.html',
  styleUrls: [
    './equipments.component.scss'
  ],
  providers: [INPUT_FIELD_VALUE_ACCESSOR]
})
export class EquipmentsComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input() hint: boolean;
  @Input() floatLabel: boolean;
  @Input() appearance = 'outline';
  @Input() locationsId: string;
  @Input() segment: Segment;
  public equipments: Array<Equipment> = [];
  public inputValue = new FormControl();
  public selectedEquipment: Equipment;
  public loading = false;

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private equipmentsApi: EquipmentsApi
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
    if (this.required) { this.inputValue.setValidators(ValidateAutocomplete); }
  }

  ngOnInit(): void {
    if (this.disabled) { this.inputValue.disable(); }
    this.inputValue.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.equipments = [];
      if (value) {
        this.searchEquipments({
          name_or_number_contains: value,
          locationId_in: this.locationsId,
          segmentId_in: this.segment.id
        });
      }
    });
  }

  ngOnChanges(changes: { [property: string]: SimpleChange }): void {
    for (const propertyName in changes) {
      if (propertyName === 'disabled') {
        const disabledValue = changes[propertyName].currentValue;
        if (disabledValue) {
          this.inputValue.disable();
        } else {
          this.inputValue.enable();
        }
      }
    }
  }

  onChange: (_: any) => void = () => { };
  onTouched: (_: any) => void = () => { };

  searchEquipments(query?): void {
    this.loading = true;
    this.equipmentsApi.getEquipments(query).subscribe(((result) => {
      this.loading = false;
      this.equipments = result.items;
    }));
  }

  writeValue(equipmentId: any): void {
    if (equipmentId) {
      this.equipmentsApi.searchOne(equipmentId).subscribe((equipment) => {
        this.selectedEquipment = equipment;
        this.inputValue.setErrors(null);
      });
    } else {
      this.unselectEquipment();
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setSelectedEquipment(): void {
    if (this.inputValue.value) {
      this.selectedEquipment = this.equipments.find((equipment) => equipment.id === this.inputValue.value);
      this.onChange(this.inputValue.value);
      this.inputValue.setErrors(null);
    }
  }

  unselectEquipment(): void {
    this.onChange(null);
    this.inputValue.setValue(null);
    this.selectedEquipment = null;
    this.inputValue.setErrors(null);
  }
}
